import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Pagination from "../admin/Pagination";
import {
  fetchUsers,
  toggleUserStatus,
  updateUserCapabilities,
  verifyUser,
  processAccountDeletion,
  approveVendor,
  rejectVendor,
} from "../../services/userApi";
import "../../styles/admin-common.css";

const formatCurrency = (value) =>
  `₹${Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const EntityManagement = ({ title, roleFilter, subtitle, showWallet = false }) => {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [updateLoadingId, setUpdateLoadingId] = useState(null);
  const [error, setError] = useState("");

  const loadUsers = () =>
    fetchUsers({
      page,
      size,
      search: debouncedSearch || undefined,
      role: roleFilter,
    });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchUsers({
      page,
      size,
      search: debouncedSearch || undefined,
      role: roleFilter,
    })
      .then((res) => {
        if (cancelled) return;
        setEntities(res.content || []);
        setTotalPages(res.pagination?.totalPages || 1);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setEntities([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, size, debouncedSearch, roleFilter]);

  const refreshList = async () => {
    const res = await loadUsers();
    setEntities(res.content || []);
  };

  return (
    <div className="admin-page">
      <h2>{title}</h2>
      <p className="subtitle">
        {subtitle || `Search and manage ${title.toLowerCase()}. Verify accounts, toggle access, and update capabilities.`}
      </p>

      <div className="admin-filters">
        <input
          type="text"
          value={search}
          placeholder="Search by name, email, or phone..."
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        />
      </div>

      {error && <p className="error-msg">{error}</p>}

      {loading ? (
        <div className="admin-loading">Loading</div>
      ) : (
        <>
          <div className="table-scroll">
            <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Seller</th>
                <th>Vendor</th>
                <th>Last active</th>
                {showWallet && (
                  <>
                    <th>Balance</th>
                    <th>On hold</th>
                    <th>Available</th>
                  </>
                )}
                {roleFilter === "BUYER" && <th>Vendor status</th>}
                <th>Verified</th>
                <th>Active</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entities.length === 0 && (
                <tr><td colSpan={(showWallet ? 13 : 10) + (roleFilter === "BUYER" ? 1 : 0)} className="empty-msg">No results</td></tr>
              )}
              {entities.map((entity) => (
                <EntityRow
                  key={entity.id}
                  entity={entity}
                  showWallet={showWallet}
                  showVendorStatus={roleFilter === "BUYER"}
                  loading={updateLoadingId === entity.id}
                  onView={() => navigate(`/users/${entity.id}`)}
                  onAction={async (action) => {
                    setUpdateLoadingId(entity.id);
                    try {
                      await action();
                      await refreshList();
                    } catch (err) {
                      setError(err.message);
                    }
                    setUpdateLoadingId(null);
                  }}
                />
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
};

const formatRole = (role) => {
  if (role === "USER") return "Seller";
  if (role === "BUYER") return "Vendor";
  if (role === "ADMIN") return "Admin";
  return role || "-";
};

const formatVendorStatus = (status) => {
  if (!status || status === "NONE") return "Not started";
  if (status === "PENDING") return "Pending";
  if (status === "APPROVED") return "Approved";
  if (status === "REJECTED") return "Rejected";
  return status;
};

const EntityRow = ({ entity, onAction, onView, loading, showWallet, showVendorStatus }) => {
  const [sellerEnabled, setSellerEnabled] = useState(Boolean(entity.sellerEnabled));
  const [buyerEnabled, setBuyerEnabled] = useState(Boolean(entity.buyerEnabled));
  const isAdmin = entity.role === "ADMIN";
  const vendorStatus = entity.vendorApprovalStatus;
  const capabilitiesDirty =
    sellerEnabled !== Boolean(entity.sellerEnabled) ||
    buyerEnabled !== Boolean(entity.buyerEnabled);

  useEffect(() => {
    setSellerEnabled(Boolean(entity.sellerEnabled));
    setBuyerEnabled(Boolean(entity.buyerEnabled));
  }, [entity.sellerEnabled, entity.buyerEnabled]);

  return (
    <tr>
      <td>
        <Link to={`/users/${entity.id}`}>{entity.name || "-"}</Link>
      </td>
      <td>{entity.email || "-"}</td>
      <td>{entity.phoneNumber || "-"}</td>
      <td>
        {isAdmin ? (
          <span className="capability-badge muted">N/A</span>
        ) : (
          <label className="capability-toggle">
            <input
              type="checkbox"
              checked={sellerEnabled}
              disabled={loading}
              onChange={(e) => setSellerEnabled(e.target.checked)}
            />
            Seller
          </label>
        )}
      </td>
      <td>
        {isAdmin ? (
          <span className="capability-badge muted">N/A</span>
        ) : (
          <label className="capability-toggle">
            <input
              type="checkbox"
              checked={buyerEnabled}
              disabled={loading}
              onChange={(e) => setBuyerEnabled(e.target.checked)}
            />
            Vendor
          </label>
        )}
      </td>
      <td>
        <span className="capability-badge">{formatRole(entity.lastActiveRole || entity.role)}</span>
      </td>
      {showWallet && (
        <>
          <td>{formatCurrency(entity.wallet?.balance)}</td>
          <td>{formatCurrency(entity.wallet?.holdAmount)}</td>
          <td className={entity.wallet?.availableBalance > 0 ? "badge-active" : ""}>
            {formatCurrency(entity.wallet?.availableBalance)}
          </td>
        </>
      )}
      {showVendorStatus && (
        <td>
          <span
            className={
              vendorStatus === "APPROVED"
                ? "badge-active"
                : vendorStatus === "PENDING"
                  ? "badge-inactive"
                  : "capability-badge muted"
            }
          >
            {formatVendorStatus(vendorStatus)}
          </span>
        </td>
      )}
      <td>{entity.isVerified ? "Yes" : "No"}</td>
      <td>
        {entity.deletedAt ? (
          <span className="capability-badge muted">Anonymized</span>
        ) : entity.deletionRequestedAt ? (
          <span className="badge-inactive">Deletion requested</span>
        ) : (
          <span className={entity.isActive !== false ? "badge-active" : "badge-inactive"}>
            {entity.isActive !== false ? "Yes" : "No"}
          </span>
        )}
      </td>
      <td>{entity.createdAt ? new Date(entity.createdAt).toLocaleDateString() : "-"}</td>
      <td className="actions-cell">
        <button type="button" className="admin-btn secondary" disabled={loading} onClick={onView}>
          View
        </button>
        {(vendorStatus === "PENDING" || vendorStatus === "REJECTED" || vendorStatus === "APPROVED") && (
          <button
            type="button"
            disabled={loading}
            className="admin-btn"
            onClick={() => onAction(() => approveVendor(entity.id))}
          >
            {vendorStatus === "APPROVED" ? "Re-stamp signature" : "Approve vendor"}
          </button>
        )}
        {vendorStatus === "PENDING" && (
          <button
            type="button"
            disabled={loading}
            className="admin-btn secondary"
            onClick={() => onAction(() => rejectVendor(entity.id))}
          >
            Reject
          </button>
        )}
        {!entity.isVerified && (
          <button type="button" disabled={loading} className="admin-btn" onClick={() => onAction(() => verifyUser(entity.id))}>
            Verify
          </button>
        )}
        <button type="button" disabled={loading} className="admin-btn secondary" onClick={() => onAction(() => toggleUserStatus(entity.id))}>
          Toggle
        </button>
        {!isAdmin && entity.deletionRequestedAt && !entity.deletedAt && (
          <button
            type="button"
            disabled={loading}
            className="admin-btn secondary"
            onClick={() => onAction(() => processAccountDeletion(entity.id))}
          >
            Process deletion
          </button>
        )}
        {!isAdmin && (
          <button
            type="button"
            disabled={loading || !capabilitiesDirty}
            className="admin-btn"
            onClick={() => onAction(() => updateUserCapabilities(entity.id, { sellerEnabled, buyerEnabled }))}
          >
            Save access
          </button>
        )}
      </td>
    </tr>
  );
};

export default EntityManagement;
